/* eslint-disable jsx-a11y/label-has-associated-control */
import { Content } from "@iventis/translations/content/typed-content";
import React, { useState } from "react";
import { Body1 } from "@iventis/styles/src/components/texts";
import styled from "@emotion/styled";
import { StyledColumnFlex } from "@iventis/styles/src/components/forms";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { IVENTIS_LOGO } from "@iventis/utilities";
import { SolidButton } from "@iventis/styles";
import LoginForm from "./login-form";
import LoginEmailForm from "./login-email-form";
import { LoginEnteredEmail } from "./login-entered-email";

export interface LoginProps {
    ssoEnabled: boolean;
    loading: boolean;
    submitLocalLogin: (email: string, password: string) => void;
    ssoLoginClicked?: () => void;
    forgottenPasswordClicked: () => void;
    loginError: string;
    signUpUrl: string;
    userEmailConfirmed: (email: string) => Promise<void>; // When the user confirms their email and clicks continue, this function will be called.
    logoUrl?: string; // Project logo. If undefined, the Iventis logo will be used.
    initialEmailValue: string; // Initial value for the email input. If null, the email input will be empty.
}

export const LoginComponent: React.FC<LoginProps> = ({
    submitLocalLogin,
    ssoLoginClicked,
    forgottenPasswordClicked,
    loginError,
    signUpUrl,
    userEmailConfirmed,
    logoUrl,
    initialEmailValue,
    ssoEnabled,
}) => {
    const [emailEntered, setEmailEntered] = useState<boolean>(initialEmailValue != null && initialEmailValue !== "");
    const [emailInput, setEmailInput] = useState<string>(initialEmailValue ?? "");
    const [loginType, setLoginType] = useState<"LOCAL" | "SSO">(ssoEnabled ? "SSO" : "LOCAL");
    const translate = useIventisTranslate();

    const onConfirmEmailClicked = async (email: string) => {
        await userEmailConfirmed(email);
        setEmailEntered(true);
    };

    return (
        <>
            <StyledLogoContainer>
                <StyledLogo data-testid="logo" id="logo-small" src={logoUrl ?? IVENTIS_LOGO} alt="Company logo" />
            </StyledLogoContainer>
            {loginType === "LOCAL" && (
                <StyledColumnFlex>
                    <LoginEnteredEmail emailEntered={emailEntered} emailInput={emailInput} translate={translate} setEmailEntered={setEmailEntered} />
                </StyledColumnFlex>
            )}
            <StyledColumnFlex data-testid="login-component">
                {!emailEntered && loginType === "LOCAL" && (
                    <>
                        <LoginEmailForm emailInput={emailInput} setEmailInput={setEmailInput} onConfirmEmailClicked={onConfirmEmailClicked} />
                        <Body1 style={{ marginTop: "20px" }}>
                            {translate(Content.login.options.dontHaveIventis)}{" "}
                            <StyledRegisterLink href={signUpUrl} style={{ textDecoration: "underline" }}>
                                {translate(Content.login.links.register)}
                            </StyledRegisterLink>
                        </Body1>
                    </>
                )}
                {emailEntered && loginType === "LOCAL" && (
                    <>
                        <LoginForm
                            showSsoLink={ssoEnabled}
                            ssoLinkClicked={() => setLoginType("SSO")}
                            login={submitLocalLogin}
                            initEmail={emailInput}
                            loginError={loginError}
                            forgottenPasswordClicked={() => {
                                forgottenPasswordClicked();
                            }}
                        />
                    </>
                )}
                {loginType === "SSO" && (
                    <>
                        <SolidButton data-testid="sign-in-with-sso-button" onClick={ssoLoginClicked} type="button">
                            {translate(Content.login.links.loginWithSingleSignOn)}
                        </SolidButton>

                        <Body1 style={{ marginTop: "20px" }}>
                            {translate(Content.login.options.alreadyHaveAccount)}{" "}
                            <StyledRegisterLink data-testid="sign-in-local" onClick={() => setLoginType("LOCAL")} style={{ textDecoration: "underline" }}>
                                {translate(Content.login.links.signIn)}
                            </StyledRegisterLink>
                        </Body1>
                    </>
                )}
            </StyledColumnFlex>
        </>
    );
};

const StyledRegisterLink = styled.a`
    color: inherit;
    cursor: pointer;
`;

const StyledLogoContainer = styled.div`
    height: 150px;
    width: 200px;
    margin: auto;
`;

const StyledLogo = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
`;
